// src/pages/document/EntityDocumentConfigPage.tsx
// Page wrapper: loads document policy for an entity and renders DocumentPolicyEditor.

import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import { useScope } from '@/hooks/useScope';
import { ErrorAlert, Alert } from '@react/components';
import { ServiceConfigPanel } from '@/components/services/ServiceConfigPanel';
import { DocumentPolicyEditor } from '@/components/document/DocumentPolicyEditor';

interface EntityDocumentConfigPageProps {
  entityType: string;
}

export const EntityDocumentConfigPage = observer(({ entityType }: EntityDocumentConfigPageProps) => {
  const { id: entityId } = useParams<{ id: string }>();
  const { t } = useI18n();
  const store = useDocument();
  const scope = useScope();
  const [policy, setPolicy] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!entityId) return;

    const load = async () => {
      setIsLoading(true);
      try {
        const result = await store.GetDocumentPolicyByEntity(entityType, entityId);
        setPolicy(result);
      } catch (err) {
        // No policy exists yet — not an error
        setPolicy(null);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [entityId, entityType, store]);

  const handleSave = useCallback(async (data: any) => {
    if (policy?.entity?.id) {
      await store.UpdateDocumentPolicy(policy.entity.id, data);
    } else {
      await store.CreateDocumentPolicy(data);
    }
    // Reload
    const updated = await store.GetDocumentPolicyByEntity(entityType, entityId!);
    setPolicy(updated);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }, [policy, entityType, entityId, store, scope.productId]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 bg-muted rounded animate-pulse" />
        <div className="h-64 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <ServiceConfigPanel
      serviceTitle={t('services.document.title')}
      backPath={`/${entityType}s/${entityId}/services`}
    >
      {saved && (
        <Alert variant="success" dismissible onDismiss={() => setSaved(false)}>
          {t('document.saveSuccess')}
        </Alert>
      )}

      {error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}

      <DocumentPolicyEditor
        policy={policy}
        entityType={entityType}
        entityId={entityId!}
        onSave={handleSave}
      />
    </ServiceConfigPanel>
  );
});

export default EntityDocumentConfigPage;
